import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import { TableExplorer } from "./components/TableExplorer";
import { TypeManager } from "@/components/TypeManager";
import { CodeManager } from "@/components/CodeManager";
import { SearchResolve } from "@/components/SearchResolve";
import { DataQuality } from "@/components/DataQuality";
import { ImportExport } from "@/components/ImportExport";
import { AuditLog } from "@/components/AuditLog";
import { AIConsole } from "@/components/AIConsole";
import { Settings } from "@/components/Settings";
import NotFound from "@/pages/not-found";
import { RedirectingScreen } from "./components/LoadingSpinner";
import { useEffect } from "react";
import { useAuthMe } from "./hooks/useLocalStorage";
// Removed automatic reset/seed on app load to preserve data across reloads

function Router() {
  return (
    <Layout>
      <Switch>
        <Route path="/" component={TableExplorer} />
        <Route path="/types" component={TypeManager} />
        <Route path="/search" component={SearchResolve} />
        <Route path="/quality" component={DataQuality} />
        <Route path="/import" component={ImportExport} />
        <Route path="/audit" component={AuditLog} />
        <Route path="/ai" component={AIConsole} />
        <Route path="/settings" component={Settings} />
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError } = useAuthMe();

  useEffect(() => {
    if (isError) {
      console.log("Authentication check failed");
    }
  }, [isError]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">redirecting...</p>
        </div>
      </div>
    );
  }

  if (data) {
    return <>{children}</>;
  }

  return (
    <RedirectingScreen />
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AuthGuard>
          <Router />
        </AuthGuard>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
